<!--
  ClientWorkspaceHeader.vue -- top section of the Client detail page.

  Shows identity (name heading + a labeled "Company name" field and the
  status, both auto-saving) +
  invite/login health summary + the status-aware action row. The contact
  (email, phone) is NOT repeated here: it lives on ClientContactCard. The actions surface conditionally:

    * Resend invitation -- visible when the latest invitation is in
      'invited' or 'expired' state, AND caller holds client.invite_admin
    * Revoke access -- visible when latest invitation is in 'invited',
      AND caller holds client.invite_admin
    * (There is no Edit button: the detail page edits inline.)

  Revoke fires a confirm dialog (the action is destructive to the
  recipient's access link). Resend / Edit fire directly.
-->
<template>
    <header class="cwh" data-testid="client-workspace-header">
        <div class="cwh__primary">
            <!-- The saved name is always the page heading. Editors get a
                 separate, visibly labeled "Company name" field below it:
                 the old editor was styled AS the heading (transparent
                 border, no label), so nobody could tell it was a field
                 (Jake 2026-10-27: "no place anywhere in the app to change a
                 client company name"). The contact email/phone live on the
                 Client contact card below. -->
            <h1 class="cwh__name" data-testid="client-workspace-name">
                {{ client.name }}
            </h1>
            <div v-if="canEdit" class="cwh__field">
                <label
                    :for="nameInputId"
                    class="cwh__field-label"
                    data-testid="client-workspace-name-label"
                >Company name</label>
                <input
                    :id="nameInputId"
                    v-model="nameDraft"
                    type="text"
                    maxlength="255"
                    autocomplete="organization"
                    class="cwh__name-input"
                    data-testid="client-workspace-name-input"
                    @input="onNameInput"
                />
            </div>
            <p
                v-if="nameError"
                class="cwh__field-error"
                data-testid="client-workspace-name-error"
            >
                {{ nameError }}
            </p>
            <p
                v-if="canEdit"
                class="cwh__field-status"
                data-testid="client-workspace-name-status"
            >
                {{ statusLabel(nameState) }}
            </p>
            <p class="cwh__status-row">
                <label v-if="canEdit" class="cwh__status-label">
                    <span>Status</span>
                    <select
                        :value="client.status || 'active'"
                        class="cwh__status-select"
                        data-testid="client-workspace-status-select"
                        @change="onStatusChange($event.target.value)"
                    >
                        <option value="lead">Lead</option>
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                    </select>
                    <span
                        class="cwh__field-status"
                        data-testid="client-workspace-status-state"
                    >
                        {{ statusLabel(statusState) }}
                    </span>
                </label>
            </p>
            <p class="cwh__health" data-testid="client-workspace-header-health">
                {{ healthCopy }}
            </p>
        </div>

        <div class="cwh__actions">
            <!-- "View as client": impersonate this OWN client into the
                 Client Portal (card https://trello.com/c/k8m2iLzG). Gated on
                 distributor.impersonate_customer (Owner / Builder / Sales
                 Executive) via can-view-as-customer, and hidden for inactive
                 clients. DISABLED (with a tooltip) when the client is not
                 impersonatable -- it has no active portal-capable account, so
                 entering the portal would 403. Mirrors the list page
                 (ClientsListPage.vue). The backend re-checks ownership + the
                 portal-capable/inactive gates (422) as a safety net for stale
                 data. -->
            <button
                v-if="canViewAsCustomer && client.status !== 'inactive'"
                type="button"
                class="cwh__btn"
                data-testid="client-workspace-view-as-customer-btn"
                :disabled="!impersonatable"
                :title="impersonatable ? '' : 'This client has no active portal account yet'"
                @click="$emit('view-as-customer')"
            >
                View as client
            </button>
            <button
                v-if="showResend"
                type="button"
                class="cwh__btn"
                data-testid="client-workspace-resend-btn"
                @click="$emit('resend')"
            >
                Resend invitation
            </button>
            <button
                v-if="showRevoke"
                type="button"
                class="cwh__btn cwh__btn--danger"
                data-testid="client-workspace-revoke-btn"
                @click="confirmRevoke = true"
            >
                Revoke access
            </button>
        </div>

        <div
            v-if="confirmRevoke"
            class="cwh__confirm-backdrop"
            data-testid="client-workspace-revoke-confirm"
        >
            <div class="cwh__confirm">
                <h3>Revoke this invitation?</h3>
                <p>
                    The Client's access link will stop working. You can
                    send a fresh invitation any time.
                </p>
                <div class="cwh__confirm-actions">
                    <button
                        type="button"
                        class="cwh__btn"
                        data-testid="client-workspace-revoke-cancel"
                        @click="confirmRevoke = false"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        class="cwh__btn cwh__btn--danger"
                        data-testid="client-workspace-revoke-confirm-btn"
                        @click="doRevoke"
                    >
                        Revoke access
                    </button>
                </div>
            </div>
        </div>
    </header>
</template>

<script>
import { useClientsWorkspaceStore } from '@/stores/clientsWorkspace'

function daysSince(iso) {
    if (!iso) return null
    const t = Date.parse(iso)
    if (Number.isNaN(t)) return null
    return Math.floor((Date.now() - t) / (24 * 60 * 60 * 1000))
}

function daysUntil(iso) {
    if (!iso) return null
    const t = Date.parse(iso)
    if (Number.isNaN(t)) return null
    return Math.ceil((t - Date.now()) / (24 * 60 * 60 * 1000))
}

export default {
    name: 'ClientWorkspaceHeader',
    props: {
        client: { type: Object, required: true },
        canInvite: { type: Boolean, default: false },
        canEdit: { type: Boolean, default: false },
        canViewAsCustomer: { type: Boolean, default: false },
        // Whether this client can be "viewed as" -- False disables the
        // button with a tooltip (no active portal-capable account).
        // Sourced from GET /clients/<id>/impersonatable by the detail page.
        impersonatable: { type: Boolean, default: false },
    },
    emits: ['resend', 'revoke', 'view-as-customer', 'saved'],
    data() {
        return {
            confirmRevoke: false,
            nameDraft: this.client.name || '',
            nameError: '',
        }
    },
    watch: {
        // Never let a reload / save echo overwrite a name being typed.
        'client.name'(v) {
            const busy =
                this.nameState === 'pending' || this.nameState === 'saving'
            if (!busy) this.nameDraft = v || ''
        },
    },
    computed: {
        store() {
            return useClientsWorkspaceStore()
        },
        nameInputId() {
            return `client-workspace-name-input-${this.client.id}`
        },
        nameState() {
            return this.store.fieldSaveState[`${this.client.id}:name`] || 'idle'
        },
        statusState() {
            return (
                this.store.fieldSaveState[`${this.client.id}:status`] || 'idle'
            )
        },
        latest() {
            return this.client.latest_invitation || null
        },
        invitationStatus() {
            // ClientInvitation.derived_status: invited / expired /
            // accepted / revoked / never_invited.
            if (!this.latest) return 'never_invited'
            return this.latest.derived_status || this.latest.status
        },
        showResend() {
            if (!this.canInvite) return false
            return (
                this.invitationStatus === 'invited' ||
                this.invitationStatus === 'expired' ||
                this.invitationStatus === 'revoked' ||
                this.invitationStatus === 'never_invited'
            )
        },
        showRevoke() {
            if (!this.canInvite || !this.latest) return false
            return this.invitationStatus === 'invited'
        },
        healthCopy() {
            // Matter-of-fact one-line health summary. The detail
            // sections carry the full story; this is the
            // glance-friendly version.
            if (!this.latest) {
                return 'No invitation sent yet.'
            }
            const status = this.invitationStatus
            if (status === 'accepted') {
                const since = daysSince(this.latest.accepted_at)
                if (since == null) return 'Active'
                if (since === 0) return 'Active since today'
                if (since === 1) return 'Active since yesterday'
                return `Active since ${since} days ago`
            }
            if (status === 'invited') {
                const sent = daysSince(this.latest.created_at)
                const expiresIn = daysUntil(this.latest.expires_at)
                if (expiresIn != null && expiresIn >= 0) {
                    return `Invitation pending, sent ${sent} day${sent === 1 ? '' : 's'} ago, expires in ${expiresIn} day${expiresIn === 1 ? '' : 's'}.`
                }
                return `Invitation pending, sent ${sent} day${sent === 1 ? '' : 's'} ago.`
            }
            if (status === 'expired') {
                const since = daysSince(this.latest.expires_at)
                return since != null
                    ? `Invitation expired ${since} day${since === 1 ? '' : 's'} ago. Resend to try again.`
                    : 'Invitation expired. Resend to try again.'
            }
            if (status === 'revoked') {
                return 'Invitation revoked. Send a new one to restore access.'
            }
            return ''
        },
    },
    methods: {
        doRevoke() {
            this.confirmRevoke = false
            this.$emit('revoke')
        },
        statusLabel(state) {
            if (state === 'pending') return 'Editing...'
            if (state === 'saving') return 'Saving...'
            if (state === 'saved') return 'Saved'
            if (state === 'error') {
                return 'Could not save. Try again.'
            }
            return ''
        },
        onNameInput() {
            const next = (this.nameDraft || '').trim()
            if (!next) {
                this.nameError = 'Name is required.'
                // Drop any earlier, still-pending edit: it must not save
                // while the screen says a name is required.
                this.store.cancelClientField(this.client.id, 'name')
                return
            }
            this.nameError = ''
            // Back to what the server has (or is about to have): nothing to
            // save, and a not-yet-sent edit must not stay armed.
            const sent = this.store.lastSentValue(this.client.id, 'name')
            const baseline = sent !== undefined ? sent : this.client.name
            if (next === baseline) {
                this.store.cancelClientField(this.client.id, 'name')
                return
            }
            this.store
                .saveClientField(this.client.id, 'name', next)
                .then((ok) => {
                    if (ok) {
                        this.$emit('saved', { field: 'name', value: next })
                    } else {
                        const msg =
                            this.store.fieldSaveError[`${this.client.id}:name`]
                        if (msg) this.nameError = msg
                    }
                })
        },
        async onStatusChange(value) {
            if (value === (this.client.status || 'active')) return
            const ok = await this.store.saveClientField(
                this.client.id,
                'status',
                value,
                { delayMs: 0 },
            )
            if (ok) this.$emit('saved', { field: 'status', value })
        },
    },
}
</script>

<style scoped>
.cwh {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    padding: 20px;
    background: var(--tf-bg, #fff);
    border: 1px solid var(--tf-border, #d0d4dc);
    border-radius: var(--tf-radius-sm, 6px);
}
.cwh__primary {
    flex: 1;
    min-width: 0;
}
.cwh__name {
    margin: 0 0 6px;
    font-size: 22px;
    font-weight: 600;
    color: var(--tf-fg-1);
}
.cwh__field {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin: 4px 0 4px;
    max-width: 480px;
}
.cwh__field-label {
    font-size: 12px;
    font-weight: 600;
    color: var(--tf-fg-2);
}
.cwh__name-input {
    width: 100%;
    padding: 6px 8px;
    border: 1px solid var(--tf-border, #d0d4dc);
    border-radius: var(--tf-radius-xs, 4px);
    background: var(--tf-bg, #fff);
    color: var(--tf-fg-1);
    font-family: inherit;
    font-size: 14px;
}
.cwh__name-input:focus {
    border-color: var(--tf-accent, #2c5fff);
    outline: none;
}
.cwh__field-error {
    margin: 0 0 4px;
    font-size: 12px;
    color: #c0392b;
}
.cwh__field-status {
    margin: 0 0 4px;
    font-size: 12px;
    color: var(--tf-fg-3, #6b7280);
    min-height: 16px;
}
.cwh__status-row {
    margin: 0 0 8px;
}
.cwh__status-label {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: var(--tf-fg-2);
}
.cwh__status-select {
    padding: 4px 8px;
    border: 1px solid var(--tf-border, #d0d4dc);
    border-radius: var(--tf-radius-xs, 4px);
    font-size: 13px;
}
.cwh__health {
    margin: 0;
    font-size: 13px;
    color: var(--tf-fg-2);
}
.cwh__actions {
    display: flex;
    gap: 8px;
    align-items: flex-start;
    flex-wrap: wrap;
}
.cwh__btn {
    padding: 6px 12px;
    border: 1px solid var(--tf-border-strong, #98a0ad);
    background: var(--tf-bg, #fff);
    color: var(--tf-fg-1);
    font-size: 13px;
    border-radius: var(--tf-radius-xs, 4px);
    cursor: pointer;
}
.cwh__btn:hover {
    background: var(--tf-surface-alt, #f4f5f7);
}
.cwh__btn--danger {
    color: #c0392b;
    border-color: #c0392b;
}
.cwh__confirm-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.4);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
}
.cwh__confirm {
    background: var(--tf-bg, #fff);
    padding: 24px;
    border-radius: var(--tf-radius-sm, 6px);
    max-width: 420px;
    width: 90%;
}
.cwh__confirm h3 {
    margin: 0 0 8px;
}
.cwh__confirm p {
    margin: 0 0 16px;
    color: var(--tf-fg-2);
}
.cwh__confirm-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
}
</style>
